import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Slider } from "../components/ui/slider";
import { ArrowLeft, Download, Music, Heart, Trash2, Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { downloadQuoteCard } from "../utils/quoteCard";

const LibraryPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [audioLibrary, setAudioLibrary] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [activePlaylist, setActivePlaylist] = useState(null);
  const [speed, setSpeed] = useState(1.0);
  const [playingId, setPlayingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    if (user.subscription_status !== "active") {
      toast.error("Active subscription required");
      navigate("/browse");
      return;
    }
    Promise.all([fetchLibrary(), fetchFavorites(), fetchPlaylists()]).finally(() =>
      setLoading(false)
    );
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, [user]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  const fetchLibrary = async () => {
    try {
      const res = await axios.get(`${API}/audio/my-library`, { headers: authHeader() });
      setAudioLibrary(res.data);
    } catch (e) {
      toast.error("Failed to load library");
    }
  };

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites`, { headers: authHeader() });
      setFavorites(res.data);
    } catch (e) {}
  };

  const fetchPlaylists = async () => {
    try {
      const res = await axios.get(`${API}/playlists`, { headers: authHeader() });
      setPlaylists(res.data);
    } catch (e) {}
  };

  const downloadAudio = async (audioId, quote) => {
    try {
      const response = await axios.get(`${API}/audio/${audioId}/download`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${quote.author.replace(/\s+/g, "_")}_${audioId.substring(0, 8)}.mp3`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Audio downloaded!");
    } catch (e) {
      toast.error("Failed to download audio");
    }
  };

  const playLibraryAudio = async (audioId) => {
    if (playingId === audioId && audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/audio/${audioId}/download`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audio.playbackRate = speed;
      audioRef.current = audio;
      audio.play();
      setPlayingId(audioId);
      audio.onended = () => setPlayingId(null);
    } catch (e) {
      toast.error("Failed to play audio");
    }
  };

  const removeFavorite = async (quoteId) => {
    try {
      await axios.post(
        `${API}/favorites/toggle`,
        { quote_id: quoteId },
        { headers: authHeader() }
      );
      setFavorites(favorites.filter((f) => f.quote_id !== quoteId));
      toast.success("Removed from favorites");
    } catch (e) {
      toast.error("Failed to remove favorite");
    }
  };

  const openPlaylist = async (playlistId) => {
    try {
      const res = await axios.get(`${API}/playlists/${playlistId}`, { headers: authHeader() });
      setActivePlaylist(res.data);
    } catch (e) {
      toast.error("Failed to load playlist");
    }
  };

  const removeFromPlaylist = async (audioId) => {
    if (!activePlaylist) return;
    try {
      await axios.put(
        `${API}/playlists/${activePlaylist.id}`,
        { audio_id: audioId, action: "remove" },
        { headers: authHeader() }
      );
      openPlaylist(activePlaylist.id);
      toast.success("Removed from playlist");
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const deletePlaylist = async (playlistId) => {
    try {
      await axios.delete(`${API}/playlists/${playlistId}`, { headers: authHeader() });
      setPlaylists(playlists.filter((p) => p.id !== playlistId));
      if (activePlaylist?.id === playlistId) setActivePlaylist(null);
      toast.success("Playlist deleted");
    } catch (e) {
      toast.error("Failed to delete playlist");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white text-sm pulse uppercase tracking-widest">Loading library...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-sm border-b border-amber-200/15">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <button
              data-testid="back-browse-btn"
              onClick={() => navigate("/browse")}
              className="text-xs uppercase tracking-[0.25em] text-amber-200/80 hover:text-amber-200 transition-colors font-light flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Browse
            </button>
            <span className="script-font text-3xl gold-text">My Library</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-[10px] uppercase tracking-[0.3em] text-amber-200/50 cinzel">
              Speed {speed.toFixed(2)}x
            </span>
            <div className="w-32">
              <Slider
                data-testid="library-speed-slider"
                min={0.5}
                max={2}
                step={0.05}
                value={[speed]}
                onValueChange={(v) => setSpeed(v[0])}
              />
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-12 px-8">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="audio" className="w-full">
            <TabsList className="bg-zinc-900 border border-white/10 mb-8">
              <TabsTrigger value="audio" data-testid="tab-audio" className="data-[state=active]:bg-white data-[state=active]:text-black text-xs uppercase tracking-wider">
                Audio ({audioLibrary.length})
              </TabsTrigger>
              <TabsTrigger value="favorites" data-testid="tab-favorites" className="data-[state=active]:bg-white data-[state=active]:text-black text-xs uppercase tracking-wider">
                Favorites ({favorites.length})
              </TabsTrigger>
              <TabsTrigger value="playlists" data-testid="tab-playlists" className="data-[state=active]:bg-white data-[state=active]:text-black text-xs uppercase tracking-wider">
                Playlists ({playlists.length})
              </TabsTrigger>
            </TabsList>

            {/* AUDIO LIBRARY */}
            <TabsContent value="audio">
              {audioLibrary.length === 0 ? (
                <EmptyState
                  icon={<Music className="w-16 h-16 text-gray-800 mx-auto mb-8" />}
                  title="Your library is empty"
                  subtitle="Generate audio from quotes to build your collection"
                  cta={() => navigate("/browse")}
                />
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {audioLibrary.map((item, index) => (
                    <Card key={item.id} data-testid={`library-card-${index}`} className="bg-black border border-white/10 hover:border-white/30 transition-all">
                      <CardContent className="p-8 space-y-6">
                        {item.quote && (
                          <>
                            <div className="min-h-[100px]">
                              <p className="text-sm text-gray-300 italic leading-relaxed font-light line-clamp-4">
                                &ldquo;{item.quote.text}&rdquo;
                              </p>
                            </div>
                            <div className="border-t border-white/10 pt-6 space-y-2">
                              <p className="text-xs uppercase tracking-wider font-light">{item.quote.author}</p>
                              <p className="text-xs text-gray-600 font-light">{item.quote.source}</p>
                              <div className="flex items-center space-x-4 mt-4 text-[10px] uppercase tracking-wider text-gray-600">
                                <span>Voice: {item.voice}</span>
                                {item.language && item.language !== "english" && (
                                  <span>· {item.language}</span>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                        <div className="flex space-x-2">
                          <Button
                            data-testid={`library-play-btn-${index}`}
                            onClick={() => playLibraryAudio(item.id)}
                            className="flex-1 bg-white text-black hover:bg-gray-200 text-xs uppercase tracking-wider font-light"
                          >
                            {playingId === item.id ? (
                              <><Pause className="w-3 h-3 mr-2" /> Pause</>
                            ) : (
                              <><Play className="w-3 h-3 mr-2" /> Play</>
                            )}
                          </Button>
                          <Button
                            data-testid={`download-btn-${index}`}
                            onClick={() => downloadAudio(item.id, item.quote)}
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* FAVORITES */}
            <TabsContent value="favorites">
              {favorites.length === 0 ? (
                <EmptyState
                  icon={<Heart className="w-16 h-16 text-gray-800 mx-auto mb-8" />}
                  title="No favorites yet"
                  subtitle="Tap the heart icon on any quote to save it here"
                  cta={() => navigate("/browse")}
                />
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {favorites.map((fav, index) => (
                    <Card key={fav.id} data-testid={`favorite-card-${index}`} className="bg-black border border-white/10 hover:border-white/30 transition-all">
                      <CardContent className="p-8 space-y-6">
                        <div className="flex justify-between items-start">
                          <button
                            data-testid={`unfavorite-btn-${index}`}
                            onClick={() => removeFavorite(fav.quote_id)}
                            className="text-rose-400 hover:text-rose-300"
                            aria-label="Remove favorite"
                          >
                            <Heart className="w-4 h-4 fill-rose-400" />
                          </button>
                          <button
                            data-testid={`fav-share-btn-${index}`}
                            onClick={() => {
                              downloadQuoteCard(fav.quote);
                              toast.success("Quote card downloaded");
                            }}
                            className="text-gray-500 hover:text-white text-[10px] uppercase tracking-wider"
                          >
                            Share PNG
                          </button>
                        </div>
                        <p className="text-sm text-gray-300 italic font-light min-h-[100px]">
                          &ldquo;{fav.quote.text}&rdquo;
                        </p>
                        <div className="border-t border-white/10 pt-6 space-y-1">
                          <p className="text-xs uppercase tracking-wider font-light">{fav.quote.author}</p>
                          <p className="text-xs text-gray-600 font-light">{fav.quote.source}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* PLAYLISTS */}
            <TabsContent value="playlists">
              {activePlaylist ? (
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <button
                      data-testid="playlist-back-btn"
                      onClick={() => setActivePlaylist(null)}
                      className="text-xs uppercase tracking-wider hover:opacity-70 flex items-center"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      All Playlists
                    </button>
                    <h2 className="script-font text-3xl">{activePlaylist.name}</h2>
                    <span className="text-xs text-gray-600">
                      {(activePlaylist.items || []).length} tracks
                    </span>
                  </div>

                  {(activePlaylist.items || []).length === 0 ? (
                    <p className="text-gray-600 text-center py-16 text-sm">This playlist is empty</p>
                  ) : (
                    <div className="space-y-3">
                      {activePlaylist.items.map((item, index) => (
                        <div
                          key={item.id}
                          data-testid={`playlist-track-${index}`}
                          className="flex items-center justify-between border border-white/10 hover:border-white/30 px-6 py-4"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-light truncate">
                              {item.quote?.text}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              {item.quote?.author} · {item.voice}
                            </p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <Button
                              data-testid={`playlist-play-${index}`}
                              onClick={() => playLibraryAudio(item.id)}
                              size="sm"
                              className="bg-white text-black hover:bg-gray-200"
                            >
                              {playingId === item.id ? (
                                <Pause className="w-3 h-3" />
                              ) : (
                                <Play className="w-3 h-3" />
                              )}
                            </Button>
                            <Button
                              data-testid={`playlist-remove-${index}`}
                              onClick={() => removeFromPlaylist(item.id)}
                              size="sm"
                              variant="outline"
                              className="border-white/20 text-white hover:bg-white/5"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : playlists.length === 0 ? (
                <EmptyState
                  icon={<Music className="w-16 h-16 text-gray-800 mx-auto mb-8" />}
                  title="No playlists yet"
                  subtitle="Tap the + icon on a quote to create your first playlist"
                  cta={() => navigate("/browse")}
                />
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {playlists.map((p, index) => (
                    <Card
                      key={p.id}
                      data-testid={`playlist-card-${index}`}
                      className="bg-black border border-white/10 hover:border-white/30 cursor-pointer transition-all"
                      onClick={() => openPlaylist(p.id)}
                    >
                      <CardContent className="p-8 space-y-4">
                        <div className="flex items-start justify-between">
                          <h3 className="script-font text-2xl">{p.name}</h3>
                          <button
                            data-testid={`delete-playlist-${index}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              deletePlaylist(p.id);
                            }}
                            className="text-gray-600 hover:text-rose-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 uppercase tracking-wider">
                          {(p.audio_ids || []).length} tracks
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ icon, title, subtitle, cta }) => (
  <div className="text-center py-32">
    {icon}
    <h2 className="text-sm uppercase tracking-widest text-gray-600 mb-6 font-light">{title}</h2>
    <p className="text-xs text-gray-700 mb-8 font-light">{subtitle}</p>
    <Button
      data-testid="empty-state-cta"
      onClick={cta}
      className="bg-white text-black hover:bg-gray-200 text-xs uppercase tracking-wider font-light"
    >
      Browse Quotes
    </Button>
  </div>
);

export default LibraryPage;
